import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { config } from '@/app/config';
import { useAppStore } from '@/app/store';
import { OWNER_ACCENT_SWATCHES } from '@/domain/types';
import { spaceRepo } from '@/repositories';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { Segmented } from '@/components/ui/Segmented';
import { toastStore } from '@/components/ui/Toast';
import { DEFAULT_BACKGROUND } from '@/storage/db';
import { clearPrivateCache } from '@/storage/db';
import {
  backgroundNotificationsGuaranteed,
  notificationsSupported,
  pickLocalImageFile,
} from '@/platform/capabilities';
import { removeUserAvatar, resolveAvatarUrl, uploadUserAvatar } from '@/platform/avatars';
import { isSupportedImageFile } from '@/platform/images';
import { MAX_WALLPAPER_BYTES, type WallpaperSource } from '@/platform/wallpapers';
import { partnerOf } from '@/domain/helpers';
import { setAppLanguage } from '@/localization/i18n';
import { listSyncQueue } from '@/sync/engine';
import { buildSyncDiagnostics, copySyncDiagnosticsText } from '@/sync/diagnostics';
import { checkForAppUpdate } from '@/features/pwa/pwaUpdate';
import styles from './SettingsView.module.css';

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function SettingsView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const plans = useAppStore((s) => s.plans);
  const preferences = useAppStore((s) => s.preferences);
  const backgroundUrl = useAppStore((s) => s.backgroundUrl);
  const wallpaperSource = useAppStore((s) => s.wallpaperSource);
  const syncStatus = useAppStore((s) => s.syncStatus);
  const online = useAppStore((s) => s.online);
  const updatePreferences = useAppStore((s) => s.updatePreferences);
  const updateProfile = useAppStore((s) => s.updateProfile);
  const setBackgroundBlob = useAppStore((s) => s.setBackgroundBlob);
  const setWallpaperSource = useAppStore((s) => s.setWallpaperSource);
  const logout = useAppStore((s) => s.logout);
  const invite = useAppStore((s) => s.invite);
  const syncNow = useAppStore((s) => s.syncNow);

  const me = profiles.find((p) => p.id === session?.user.id);
  const bg = preferences?.background ?? DEFAULT_BACKGROUND;
  const space = useAppStore((s) => s.space);
  const refreshSpaceData = useAppStore((s) => s.refreshSpaceData);

  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [busy, setBusy] = useState(false);

  if (!session) return null;

  const regenerateInvite = async () => {
    if (!space) return;
    setBusy(true);
    try {
      const code = await spaceRepo.regenerateInvite(session.user.id, space.id);
      await refreshSpaceData();
      toastStore.show({ type: 'success', message: `${t('app.inviteSent')}: ${code}` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      toastStore.show({
        type: 'error',
        message: msg.includes('full') ? t('app.spaceFull') : t('errors.generic'),
      });
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!displayName.trim()) return;
    await updateProfile({ displayName: displayName.trim() });
    toastStore.show({ type: 'success', message: t('common.save') });
  };

  const pickAvatar = async () => {
    const file = await pickLocalImageFile();
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastStore.show({ type: 'error', message: t('settings.avatarUnsupported') });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toastStore.show({ type: 'error', message: t('settings.avatarTooLarge') });
      return;
    }
    setBusy(true);
    try {
      const path = await uploadUserAvatar(session.user.id, file);
      await updateProfile({ avatarPath: path });
      toastStore.show({ type: 'success', message: t('settings.avatarUpdated') });
    } catch {
      toastStore.show({ type: 'error', message: t('errors.generic') });
    } finally {
      setBusy(false);
    }
  };

  const clearAvatar = async () => {
    if (!me?.avatarPath) return;
    setBusy(true);
    try {
      await removeUserAvatar(session.user.id, me.avatarPath);
      await updateProfile({ avatarPath: null });
      toastStore.show({ type: 'success', message: t('settings.avatarRemoved') });
    } catch {
      toastStore.show({ type: 'error', message: t('errors.generic') });
    } finally {
      setBusy(false);
    }
  };

  const partner = session ? partnerOf(profiles, session.user.id) : null;
  const hasOwnWallpaper =
    Boolean(preferences?.background?.imageId) ||
    Boolean(preferences?.background?.storagePath) ||
    wallpaperSource === 'own';

  const pickBackground = async () => {
    const file = await pickLocalImageFile();
    if (!file) return;
    if (!isSupportedImageFile(file)) {
      toastStore.show({ type: 'error', message: t('background.unsupported') });
      return;
    }
    if (file.size > MAX_WALLPAPER_BYTES) {
      toastStore.show({ type: 'error', message: t('background.tooLarge') });
      return;
    }
    try {
      await setBackgroundBlob(file);
      toastStore.show({ type: 'success', message: t('background.saved') });
    } catch {
      toastStore.show({ type: 'error', message: t('background.corrupt') });
    }
  };

  const changeWallpaperMode = async (source: WallpaperSource) => {
    try {
      await setWallpaperSource(source);
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      if (code === 'no_own_wallpaper') {
        toastStore.show({ type: 'error', message: t('background.needOwnFirst') });
        return;
      }
      if (code === 'no_partner') {
        toastStore.show({ type: 'error', message: t('background.noPartner') });
        return;
      }
      if (code === 'partner_wallpaper_missing') {
        toastStore.show({ type: 'error', message: t('background.partnerMissing') });
        return;
      }
      toastStore.show({ type: 'error', message: t('errors.generic') });
    }
  };

  const exportData = () => {
    const profile = profiles.find((p) => p.id === session.user.id);
    downloadJson(`twyme-export-${new Date().toISOString().slice(0, 10)}.json`, {
      exportedAt: new Date().toISOString(),
      profile,
      plans: plans.filter((p) => !p.deletedAt),
    });
    toastStore.show({ type: 'success', message: t('settings.exportData') });
  };

  const handleClearCache = async () => {
    setBusy(true);
    try {
      await clearPrivateCache();
      await logout();
      navigate('/app/auth');
      toastStore.show({ type: 'success', message: t('settings.clearCache') });
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/app/auth');
  };

  const checkAppUpdates = async () => {
    setBusy(true);
    try {
      const result = await checkForAppUpdate();
      if (result === 'updated') {
        toastStore.show({ type: 'success', message: t('settings.updateApplying') });
        return;
      }
      if (result === 'available') {
        toastStore.show({ type: 'success', message: t('settings.updateAvailableSoon') });
        return;
      }
      if (result === 'unavailable') {
        toastStore.show({ type: 'info', message: t('settings.updateUnavailable') });
        return;
      }
      toastStore.show({ type: 'success', message: t('settings.updateCurrent') });
    } catch {
      toastStore.show({ type: 'error', message: t('errors.generic') });
    } finally {
      setBusy(false);
    }
  };

  const copySyncDiagnostics = async () => {
    try {
      const queue = await listSyncQueue();
      const report = buildSyncDiagnostics({
        mode: config.mode,
        online,
        syncStatus,
        queue,
      });
      await copySyncDiagnosticsText(report);
      toastStore.show({ type: 'success', message: t('settings.syncDiagnosticsCopied') });
    } catch {
      toastStore.show({ type: 'error', message: t('errors.generic') });
    }
  };

  return (
    <div className={styles.view}>
      <h1 className={styles.pageTitle}>{t('settings.title')}</h1>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.account')}</h2>
        <p className={styles.meta}>{session.user.email}</p>
        <div className={styles.row}>
          <Button variant="secondary" size="sm" onClick={handleLogout}>
            {t('auth.signOut')}
          </Button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.profile')}</h2>
        <div className={styles.avatarRow}>
          {me && (
            <Avatar
              name={me.displayName}
              color={me.accentColor}
              size="lg"
              src={resolveAvatarUrl(me.avatarPath, me.updatedAt) ?? undefined}
            />
          )}
          <div className={styles.avatarActions}>
            <Button variant="secondary" size="sm" loading={busy} onClick={() => void pickAvatar()}>
              {t('settings.avatarPick')}
            </Button>
            {me?.avatarPath && (
              <Button variant="ghost" size="sm" loading={busy} onClick={() => void clearAvatar()}>
                {t('settings.avatarRemove')}
              </Button>
            )}
            <p className={styles.hint}>{t('settings.avatarHint')}</p>
          </div>
        </div>
        <Input
          label={t('settings.displayName')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Button variant="primary" size="sm" onClick={() => void saveProfile()}>
          {t('common.save')}
        </Button>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.planColor')}</h2>
        <p className={styles.hint}>{t('settings.planColorHint')}</p>
        <div className={styles.planColorPreview} aria-hidden>
          <span
            className={styles.planColorRail}
            style={{ background: me?.accentColor ?? OWNER_ACCENT_SWATCHES[8].hex }}
          />
          <span className={styles.planColorPreviewBody}>
            <span className={`${styles.planColorPreviewTime} mono`}>09:00–11:00</span>
            <span className={styles.planColorPreviewTitle}>{t('settings.planColorPreview')}</span>
          </span>
        </div>
        <div className={styles.colors} role="listbox" aria-label={t('settings.planColor')}>
          {OWNER_ACCENT_SWATCHES.map((swatch) => (
            <button
              key={swatch.id}
              type="button"
              className={styles.colorSwatch}
              style={{ background: swatch.hex }}
              aria-label={t(`settings.accents.${swatch.id}`)}
              aria-pressed={me?.accentColor === swatch.hex}
              title={t(`settings.accents.${swatch.id}`)}
              onClick={() => void updateProfile({ accentColor: swatch.hex })}
            >
              {me?.accentColor === swatch.hex ? <span className={styles.colorCheck}>✓</span> : null}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.pairSpace')}</h2>
        {profiles.length >= 2 ? (
          <p className={styles.meta}>{t('app.spaceFull')}</p>
        ) : invite?.codeHint ? (
          <>
            <p className={styles.meta}>
              {t('onboarding.inviteCode')}: <code>{invite.codeHint}</code>
            </p>
            <p className={styles.hint}>{t('onboarding.inviteHint')}</p>
            <div className={styles.row}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (invite.codeHint) void navigator.clipboard.writeText(invite.codeHint);
                }}
              >
                {t('onboarding.copyInvite')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={busy}
                onClick={() => void regenerateInvite()}
              >
                {t('onboarding.regenerateInvite')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className={styles.hint}>{t('app.waitingForPartner')}</p>
            <Button variant="secondary" size="sm" loading={busy} onClick={() => void regenerateInvite()}>
              {t('onboarding.regenerateInvite')}
            </Button>
          </>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.appearance')}</h2>
        <label className={styles.field}>
          <span>{t('settings.weekStarts')}</span>
          <Segmented
            value={String(preferences?.weekStartsOn ?? 1)}
            onChange={(v) => void updatePreferences({ weekStartsOn: Number(v) as 0 | 1 })}
            options={[
              { value: '0', label: t('settings.weekSunday') },
              { value: '1', label: t('settings.weekMonday') },
            ]}
          />
        </label>
        <label className={styles.field}>
          <span>{t('settings.timeFormat')}</span>
          <Segmented
            value={preferences?.timeFormat ?? '24h'}
            onChange={(v) => void updatePreferences({ timeFormat: v as '12h' | '24h' })}
            options={[
              { value: '12h', label: '12h' },
              { value: '24h', label: '24h' },
            ]}
          />
        </label>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.background')}</h2>
        <p className={styles.hint}>{t('background.modeHint')}</p>
        <Segmented
          value={wallpaperSource}
          onChange={(v) => void changeWallpaperMode(v as WallpaperSource)}
          options={[
            { value: 'none', label: t('background.modeNone') },
            { value: 'own', label: t('background.modeOwn') },
            {
              value: 'partner',
              label: partner
                ? t('background.modePartnerNamed', { name: partner.displayName })
                : t('background.modePartner'),
            },
          ]}
        />
        {backgroundUrl && (
          <div
            className={styles.preview}
            style={{ backgroundImage: `url(${backgroundUrl})` }}
          />
        )}
        <div className={styles.row}>
          <Button variant="secondary" size="sm" onClick={() => void pickBackground()}>
            {hasOwnWallpaper ? t('background.replace') : t('background.pick')}
          </Button>
          {hasOwnWallpaper && (
            <Button variant="ghost" size="sm" onClick={() => void setBackgroundBlob(null)}>
              {t('background.remove')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void updatePreferences({
                background: {
                  ...(preferences?.background ?? DEFAULT_BACKGROUND),
                  ...DEFAULT_BACKGROUND,
                  imageId: preferences?.background?.imageId ?? null,
                },
              })
            }
          >
            {t('background.restoreDefault')}
          </Button>
        </div>
        {wallpaperSource !== 'none' && (
          <>
            <SliderField
              label={t('background.dim')}
              value={bg.dim}
              min={0}
              max={0.9}
              step={0.05}
              onChange={(v) => void updatePreferences({ background: { ...bg, dim: v } })}
            />
            <SliderField
              label={t('background.blur')}
              value={bg.blur}
              min={0}
              max={24}
              step={1}
              onChange={(v) => void updatePreferences({ background: { ...bg, blur: v } })}
            />
            <SliderField
              label={t('background.opacity')}
              value={bg.surfaceOpacity}
              min={0.5}
              max={1}
              step={0.02}
              onChange={(v) => void updatePreferences({ background: { ...bg, surfaceOpacity: v } })}
            />
            <SliderField
              label={t('background.positionX')}
              value={bg.positionX}
              min={0}
              max={100}
              step={1}
              onChange={(v) => void updatePreferences({ background: { ...bg, positionX: v } })}
            />
            <SliderField
              label={t('background.positionY')}
              value={bg.positionY}
              min={0}
              max={100}
              step={1}
              onChange={(v) => void updatePreferences({ background: { ...bg, positionY: v } })}
            />
          </>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.notifications')}</h2>
        <p className={styles.hint}>{t('settings.notificationsOffDefault')}</p>
        <p className={styles.hint}>{t('settings.notificationsLimitation')}</p>
        {!backgroundNotificationsGuaranteed() && (
          <p className={styles.warn}>{t('settings.notificationsWebLimit')}</p>
        )}
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={preferences?.notifications.enabled ?? false}
            disabled={!notificationsSupported()}
            onChange={(e) =>
              void updatePreferences({
                notifications: {
                  enabled: e.target.checked,
                  offsets: preferences?.notifications.offsets ?? [],
                },
              })
            }
          />
          {t('settings.notificationsEnable')}
        </label>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.language')}</h2>
        <Segmented
          value={i18n.language.startsWith('ru') ? 'ru' : 'en'}
          onChange={(v) => {
            void updatePreferences({ locale: v as 'en' | 'ru' });
            setAppLanguage(v as 'en' | 'ru');
          }}
          options={[
            { value: 'en', label: 'English' },
            { value: 'ru', label: 'Русский' },
          ]}
        />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.sync')}</h2>
        <p className={styles.meta}>{t(`app.sync.${syncStatus === 'changes_pending' ? 'changesPending' : syncStatus === 'sync_error' ? 'syncError' : syncStatus}`)}</p>
        <p className={styles.hint}>{t('settings.syncHint')}</p>
        <div className={styles.row}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void syncNow()}
            disabled={config.mode !== 'production'}
          >
            {t('settings.syncNow')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void copySyncDiagnostics()}>
            {t('settings.copySyncDiagnostics')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
            {t('settings.reloadApp')}
          </Button>
          <Button variant="ghost" size="sm" loading={busy} onClick={() => void checkAppUpdates()}>
            {t('settings.checkForUpdates')}
          </Button>
        </div>
        <p className={styles.hint}>{t('settings.syncDiagnosticsHint')}</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.dataPrivacy')}</h2>
        <div className={styles.row}>
          <Button variant="secondary" size="sm" onClick={exportData}>
            {t('settings.exportData')}
          </Button>
          <Button variant="danger" size="sm" loading={busy} onClick={() => void handleClearCache()}>
            {t('settings.clearCache')}
          </Button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.about')}</h2>
        <p className={styles.meta}>TwyMe v{config.mode === 'demo' ? '1.0 demo' : '1.0'}</p>
        <p className={styles.hint}>{t('brand.slogan')}</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.shortcuts')}</h2>
        <ul className={styles.shortcuts}>
          <li><kbd>Ctrl</kbd>+<kbd>N</kbd> — {t('shortcuts.newPlan')}</li>
          <li><kbd>Ctrl</kbd>+<kbd>K</kbd> — {t('shortcuts.search')}</li>
          <li><kbd>Ctrl</kbd>+<kbd>,</kbd> — {t('shortcuts.settings')}</li>
          <li><kbd>T</kbd> — {t('shortcuts.today')}</li>
          <li><kbd>Esc</kbd> — {t('shortcuts.close')}</li>
        </ul>
      </section>
    </div>
  );
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className={styles.slider}>
      <span className={styles.sliderLabel}>
        {label}
        <span className={styles.sliderValue}>{Math.round(value * (step < 1 ? 100 : 1)) / (step < 1 ? 100 : 1)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
